import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import useTypingEffect from '../hooks/useTypingEffect';
import api from '../services/api';
import './SearchResults.css';

function SearchResults({ results, enableTyping = true, sessionId, messageIndex, query }) {
  const { displayedText, isTyping } = useTypingEffect(
    enableTyping ? results?.ai_response || '' : '',
    15
  );

  const [feedbackGiven, setFeedbackGiven] = useState(null); // 'positive' or 'negative'
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  if (!results) return null;

  const textToDisplay = enableTyping ? displayedText : results.ai_response;
  const references = results.structured_references || [];
  const topicCategory = results.topic_category || null;

  const handleFeedback = async (rating) => {
    if (feedbackSubmitting || feedbackGiven) return;

    setFeedbackSubmitting(true);
    try {
      await api.submitFeedback({
        run_id: results.run_id || null,
        session_id: sessionId,
        message_index: messageIndex,
        rating: rating,
        query: query
      });

      setFeedbackGiven(rating);
      console.log(`Feedback submitted: ${rating}`);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // Still set as given to avoid multiple submissions
      setFeedbackGiven(rating);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  return (
    <div className="search-results">
      {(textToDisplay || results.ai_response) && (
        <div className="ai-response-section">
          <div className="section-header">
            <h3 className="section-title">Summary based on Retrieved Context...</h3>
            {topicCategory && (
              <span className="topic-badge">{topicCategory}</span>
            )}
          </div>
          <div className="ai-response ai-response-wrapper">
            <ReactMarkdown>{textToDisplay}</ReactMarkdown>
            {isTyping && <span className="typing-cursor">▊</span>}
          </div>
          {!isTyping && references.length > 0 && (
            <div className="references-section">
              <h4 className="references-title">References</h4>
              <div className="references-list">
                {references.map((ref, idx) => (
                  <div key={idx} className="reference-card">
                    <div className="reference-primary">
                      {ref.surah_name && (
                        <span className="ref-surah">
                          {ref.surah_name}{ref.surah_number ? ` (${ref.surah_number})` : ''}
                        </span>
                      )}
                      {ref.ayah_refs && ref.ayah_refs.length > 0 && (
                        <span className="ref-ayahs">{ref.ayah_refs.join(', ')}</span>
                      )}
                    </div>
                    {(ref.section_title || ref.page_number) && (
                      <div className="reference-secondary">
                        {ref.section_title && (
                          <span className="ref-section">{ref.section_title}</span>
                        )}
                        {ref.page_number && (
                          <span className="ref-page">Page {ref.page_number}</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {!isTyping && (
            <div className="feedback-buttons">
              <button
                className={`feedback-btn ${feedbackGiven === 'positive' ? 'active positive' : ''}`}
                onClick={() => handleFeedback('positive')}
                disabled={feedbackSubmitting || feedbackGiven !== null}
                title="Helpful response"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                </svg>
              </button>
              <button
                className={`feedback-btn ${feedbackGiven === 'negative' ? 'active negative' : ''}`}
                onClick={() => handleFeedback('negative')}
                disabled={feedbackSubmitting || feedbackGiven !== null}
                title="Not helpful"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"></path>
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchResults;
